import { useCallback } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';

export interface AuthRedirectTarget {
  pathname: string;
  search: string;
  /** Router state of the original navigation, e.g. the `buyNow` payload for checkout. */
  state?: unknown;
}

export interface AuthRedirectState {
  from?: AuthRedirectTarget;
}

/** Snapshot of the page a guest was trying to reach, to be carried into login/register. */
export const buildAuthRedirectState = (location: Location): AuthRedirectState => ({
  from: {
    pathname: location.pathname,
    search: location.search,
    state: location.state ?? undefined,
  },
});

const readAuthRedirectState = (state: unknown): AuthRedirectTarget | undefined => {
  const from = (state as AuthRedirectState | null)?.from;
  // Only allow in-app absolute paths so the state cannot be used for off-site redirects.
  if (!from || typeof from.pathname !== 'string') return undefined;
  if (!from.pathname.startsWith('/') || from.pathname.startsWith('//')) return undefined;
  return from;
};

/**
 * Used by the login/register pages to send the user back where they came from
 * once they are authenticated.
 */
export const useAuthRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const from = readAuthRedirectState(location.state);

  const redirectAfterAuth = useCallback(() => {
    if (from) {
      navigate(`${from.pathname}${from.search || ''}`, { state: from.state, replace: true });
    } else {
      navigate('/');
    }
  }, [from, navigate]);

  /** Pass-through state so switching between login and register keeps the destination. */
  const authLinkState = from ? ({ from } as AuthRedirectState) : undefined;

  return { from, redirectAfterAuth, authLinkState };
};
